import { DEFAULT_SCORING, DEFAULT_SETTINGS } from '../data/scoring.js';
import {
  ESPN_STAT_MAP,
  ESPN_DST_STAT_MAP,
  ESPN_POSITION_MAP,
  ESPN_POSITION_LABELS,
  LINEUP_SLOT_MAP,
  isSuperflexSlot,
  isFlexSlot,
} from './espnConstants.js';

// ESPN league settings → Mock1 settings.
//
// Yahoo analog: parseYahooSettings in ./yahooImport.js. Same job, same
// DEFAULT_SCORING starting point, same pts-per-yard → ydsPer conversion.
// The id tables and every claim about them live in
// docs/planning/espn-constants-provenance.md; ./espnConstants.js transcribes
// that doc. Nothing about ESPN's ids is decided here.

/**
 * Parse an ESPN league `settings` object into Mock1 settings.
 *
 * Input shape (ESPN mSettings view): the `settings` object of a league
 * payload — size, draftSettings, scoringSettings.scoringItems (an array of
 * { statId, points, pointsOverrides }), rosterSettings.lineupSlotCounts.
 * src/tools/fixtures/espn-league-42654852-2025.json is a live capture of
 * exactly this shape.
 *
 * @param settings ESPN's `settings` object.
 * @param season   OPTIONAL. The season year, which is NOT inside `settings` —
 *                 ESPN puts seasonId at the top level of the league envelope,
 *                 a sibling of settings. A caller holding the envelope (or the
 *                 /api/espn/league response, which now surfaces seasonId
 *                 alongside settings) passes it here. Omitted, the parser
 *                 falls back to settings.seasonId and then null, so existing
 *                 callers are unaffected.
 *
 * Returns { name, season, budget, teams, is_auction_draft, scoring,
 * positions, notes }. `notes` holds human-readable import caveats — things
 * Mock1's model cannot represent, surfaced rather than silently dropped.
 */
export function parseEspnSettings(settings, season) {
  const scoring = { ...DEFAULT_SCORING };
  const notes = [];

  // PPR defaults to 0 unless statId 53 is explicitly present. Same rule as
  // Yahoo's stat_id 11: a league that doesn't score receptions has no
  // scoring item for them, and DEFAULT_SCORING's 0.5 would import it as
  // half-PPR. 53, NOT 41 — provenance §3, trap 1: both ids carry the same
  // value but only 53 ever appears in scoringItems, so keying on 41 imports
  // every PPR league as non-PPR. The 53 → receptions row lives in
  // ESPN_STAT_MAP; the pass below is what writes it.
  scoring.receptions = 0;

  const scoringItems = settings?.scoringSettings?.scoringItems ?? [];

  // Every statId present anywhere in the array, collected BEFORE either pass.
  // supersededBy is a whole-array question: ESPN's item order is arbitrary, so
  // checking only the items seen so far reintroduces exactly the order
  // dependence the marker exists to kill (74 vs 198 → fgMade50).
  const presentIds = new Set();
  for (const item of scoringItems) {
    const id = Number(item?.statId);
    if (!isNaN(id)) presentIds.add(id);
  }

  // TWO passes, because one pass cannot produce both scopes. The maps are
  // split by which field holds the value:
  //   player scope → item.points
  //   D/ST scope   → item.pointsOverrides["16"]
  // Reading .points for a D/ST-only stat yields 0 and silently zeroes out all
  // defense scoring — that is what the split prevents (provenance §2).
  //
  // The two passes need NO scope guard between them, and adding one is a bug.
  // Ids 101/102 are in both maps, and in the live fixture they carry
  // points: 6 AND pointsOverrides["16"]: 6 — a single item genuinely serving
  // both scopes. Each field is real, so each pass reads its own and is right.
  // An earlier revision gated pass A on "id is in the D/ST map and carries a
  // 16 override," which skipped exactly those items and left returnTD at its
  // DEFAULT_SCORING value; a league scoring return TDs at 3 imported as 6.
  // That guard was written against a hand-built fixture encoding the old §2
  // claim that ALL D/ST items carry points: 0. They do not — 7 of 27 carry a
  // real player value (93 101 102 103 104 206 209).
  //
  // `writes` is shared across both passes so first-wins holds globally — see
  // applyScoringPass for why the FIRST value wins rather than the last.
  const writes = new Map();
  applyScoringPass(scoring, scoringItems, presentIds, ESPN_STAT_MAP,
    (item) => item?.points, writes);
  applyScoringPass(scoring, scoringItems, presentIds, ESPN_DST_STAT_MAP,
    (item) => item?.pointsOverrides?.['16'], writes);

  // One note per Mock1 key whose contributing ESPN ids disagreed.
  for (const [key, w] of writes) {
    if (!w.conflictingIds.length) continue;
    notes.push(
      `Scoring: ESPN ids ${[w.statId, ...w.conflictingIds].join(', ')} all map to `
      + `"${key}" but carry different values (${[w.value, ...w.conflictingValues].join(', ')}). `
      + `Mock1 has one key for all of them, so ${w.value} (id ${w.statId}) was used.`
    );
  }

  // ── FUMBLES — convention clash, must be converted (provenance §5) ────────
  //
  // ESPN is ADDITIVE: 68 = ALL fumbles (the lost ones included), 72 = the lost
  // subset, so a lost fumble fires BOTH items. Mock1 uses Convention B
  // (partition): scoring.fumbles scores NON-lost fumbles only, and
  // scoring.fumblesLost carries the whole penalty for a lost one.
  //
  // Copying 68→fumbles and 72→fumblesLost straight across UNDER-PENALIZES
  // every lost fumble — under ESPN a lost fumble is worth 68+72, and the
  // straight copy pays only 72.
  //
  // This bug is invisible in default-league testing: league 42654852 omits 68
  // entirely, so espn68 is 0 and the two forms agree. Only a league that
  // scores all fumbles exposes it.
  //
  // Read from the raw items, NOT from `scoring` — an absent id must default
  // to 0 here, and scoring already holds DEFAULT_SCORING.fumblesLost (-2) for
  // a league that never mentioned 72.
  const espn68 = rawPlayerPoints(scoringItems, 68) ?? 0;
  const espn72 = rawPlayerPoints(scoringItems, 72) ?? 0;
  scoring.fumbles = espn68;
  scoring.fumblesLost = espn68 + espn72;

  // ── BONUSES — deliberately untouched (provenance §7.1) ───────────────────
  //
  // passYdsBonus / rushYdsBonus / recYdsBonus and their thresholds stay at
  // their DEFAULT_SCORING values. No bonus-shaped item appeared in the sampled
  // league and ESPN's mechanism for yardage bonuses is UNVERIFIED. An imported
  // ESPN league therefore carries zero bonuses, which is wrong for any league
  // scoring 350/150/150 — a known, documented limitation. Do not invent a
  // mapping to close it; derive one per §8 first.

  const { positions, positionNotes } = parsePositions(settings);
  notes.push(...positionNotes);

  const draftSettings = settings?.draftSettings;

  return {
    name: settings?.name ?? null,
    // ESPN puts the league name inside settings, but seasonId on the league
    // ENVELOPE (a sibling of settings) — THAT IS WHY THE PARAMETER EXISTS.
    // Parsing settings alone genuinely cannot recover the season; there is no
    // field to read. The explicit argument wins; settings.seasonId is a
    // fallback for a caller that spliced the envelope value in
    // ({ ...league.settings, seasonId: league.seasonId }); null when neither
    // is available, which is what the fixture yields — it captures settings
    // alone and carries no seasonId.
    season: season ?? settings?.seasonId ?? null,
    // Live value only for an auction league. On a SNAKE league ESPN still
    // reports 200 — that is its default, not a real setting (provenance §7.5),
    // which is why is_auction_draft is the field to gate on.
    budget: draftSettings?.auctionBudget ?? DEFAULT_SETTINGS.budget,
    teams: settings?.size ?? DEFAULT_SETTINGS.teams,
    is_auction_draft: draftSettings?.type === 'AUCTION',
    scoring,
    positions,
    notes,
  };
}

/**
 * Parse an ESPN `draftDetail` into the integration contract's pick objects.
 *
 * TWO-CALL JOIN. mDraftDetail carries playerId and nothing human-readable, so
 * names and positions come from a second call — api/espn/players.js, whose
 * `players` map is this function's second argument:
 *   { [playerId]: { name, posId } }, posId being ESPN's raw defaultPositionId.
 * That endpoint passes posId through UNTRANSLATED and says the consumer owns
 * the mapping; this is that consumer.
 *
 * Returns { picks, notes }. Each pick is exactly the shape
 * docs/planning/espn-import-plan.md specifies —
 *   { name, pos, amount, keeper, pickNo, rosterId }
 * — plus `nominatingTeamId`, which is auction-specific and has no Sleeper
 * equivalent, so it is worth carrying on the saved record.
 *
 * ⚠️ nominatingTeamId MUST NOT REACH THE ANONYMIZED PAYLOAD. It is a seat
 * identifier. anonymizeAuctionHistory is a closed whitelist that builds its
 * picks by hand from six named fields, so an extra key here cannot leak by
 * accident — and that whitelist stays closed. Do not add it there.
 */
export function parseEspnDraft(draftDetail, playersById) {
  const picks = [];
  const notes = [];
  const rawPicks = Array.isArray(draftDetail?.picks) ? draftDetail.picks : [];
  const lookup = playersById ?? {};

  // Dropped picks, tallied by the position that could not be mapped, so the
  // note can name them rather than just counting.
  const droppedByLabel = new Map();

  for (const p of rawPicks) {
    if (!p) continue;

    const entry = lookup[String(p.playerId)];
    const pos = ESPN_POSITION_MAP[entry?.posId];

    // A POSITION IS LOAD-BEARING; A NAME IS NOT. ESPN_POSITION_MAP returns
    // undefined for punters, IDP and head coaches BY DESIGN — Mock1 has no
    // concept of them and cannot price one. Emitting the pick with pos: null
    // would push that null straight into the price map, so the pick is
    // DROPPED and counted instead. Losing a pick Mock1 could never price is
    // the smaller error; a null-positioned row poisoning pricing is the
    // larger one.
    if (!pos) {
      const posId = entry?.posId;
      const label = ESPN_POSITION_LABELS[posId]
        ?? (posId == null ? 'unknown position' : `position id ${posId}`);
      droppedByLabel.set(label, (droppedByLabel.get(label) ?? 0) + 1);
      continue;
    }

    picks.push({
      // THE NAME is the enrichment half — never a failure mode. The entry
      // resolved (that is what got us past the pos guard above); only its name
      // is missing, which api/espn/players.js can legitimately return as null.
      // Degrade to the id: an unlabelled row at the right price still carries
      // its market datum, and the id is at least traceable. The POSITION half
      // is not like this — see the guard above, and the empty-map error in
      // fetchEspnLeagueSeason.
      name: typeof entry?.name === 'string' && entry.name ? entry.name : String(p.playerId),
      pos,
      amount: numOrNull(p.bidAmount),
      // Strict true, matching the anonymizer: a keeper is a carried contract,
      // not a market price, and a truthy non-boolean must not become one.
      keeper: p.keeper === true,
      pickNo: numOrNull(p.overallPickNumber),
      rosterId: numOrNull(p.teamId),
      nominatingTeamId: numOrNull(p.nominatingTeamId),
    });
  }

  // ONE summary note, not one per pick — a 14-team IDP league would otherwise
  // bury every other import caveat under dozens of identical lines.
  if (droppedByLabel.size) {
    const total = [...droppedByLabel.values()].reduce((a, b) => a + b, 0);
    const breakdown = [...droppedByLabel.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([label, n]) => `${n}× ${label}`)
      .join(', ');
    notes.push(
      `Draft: ${total} of ${rawPicks.length} picks were dropped — their ESPN position `
      + `has no Mock1 equivalent (${breakdown}). Mock1 cannot price these players, so `
      + `the imported draft is ${total} picks short of the real one.`
    );
  }

  // The dropped count as a NUMBER, not only as prose inside `notes`. The note
  // is for the user; this is for instrumentation, which needs to aggregate it
  // across imports. Deriving it by regexing the note would couple a metric to
  // a sentence's wording — the sentence would then be unchangeable for a
  // reason nothing near it explains.
  const droppedPicks = [...droppedByLabel.values()].reduce((a, b) => a + b, 0);

  return { picks, notes, droppedPicks };
}

// Number or null. ESPN sends these as numbers, but a missing field must land
// as null rather than NaN — finiteOrNull in the anonymizer would turn NaN into
// null anyway, and this keeps the saved record honest before it gets there.
function numOrNull(v) {
  const n = Number(v);
  return v == null || isNaN(n) ? null : n;
}

// Player-scope `points` for one statId, or undefined when the id is absent.
// Used only by the fumble conversion, which needs the raw ESPN values rather
// than whatever landed in `scoring`.
function rawPlayerPoints(scoringItems, statId) {
  const item = scoringItems.find((i) => Number(i?.statId) === statId);
  const value = Number(item?.points);
  return item && !isNaN(value) ? value : undefined;
}

// ── Roster shape ───────────────────────────────────────────────────────────
//
// ESPN lineup slot → Mock1 position. Only the DEDICATED slots, the ones whose
// eligibility is a single position. Both multi-position answers are DERIVED —
// SFLEX via isSuperflexSlot, FLEX via isFlexSlot — rather than listed, so a
// future ESPN slot with either shape is picked up for free (espnConstants.js
// explains why each rule is a subset test and not a hardcoded slot 7 or 23).
//
// Slot 23 (FLEX) used to be a row here, and that hardcoding is what dropped
// slots 3 (RB/WR) and 5 (WR/TE) — see parsePositions.
const SLOT_TO_POSITION = {
  0:  'QB',
  2:  'RB',
  4:  'WR',
  6:  'TE',
  16: 'DEF',
  17: 'K',
  20: 'BN',
};

// What Mock1's own FLEX admits: RB/WR/TE. This is Mock1's definition, not
// ESPN's, which is why it lives here and not in the constants table — it is the
// yardstick an ESPN flex slot is measured against. A slot admitting less than
// all three (RB/WR, WR/TE) still lands in FLEX, but by GENERALIZATION, and that
// is what earns a note.
const MOCK1_FLEX_POSITION_IDS = [2, 3, 4];

// IR and INV hold players but are not roster slots Mock1 drafts into —
// counting them would inflate roster size and therefore every price. The
// sampled league carries IR: 1, so this is load-bearing, not hypothetical.
const IGNORED_SLOTS = new Set([21, 22]);

function parsePositions(settings) {
  const positions = { QB: 0, RB: 0, WR: 0, TE: 0, FLEX: 0, SFLEX: 0, DEF: 0, K: 0, BN: 0 };
  const positionNotes = [];
  const slotCounts = settings?.rosterSettings?.lineupSlotCounts ?? {};

  for (const [rawSlot, rawCount] of Object.entries(slotCounts)) {
    const slot = Number(rawSlot);
    const count = Number(rawCount);
    if (isNaN(slot) || isNaN(count) || count <= 0) continue;
    if (IGNORED_SLOTS.has(slot)) continue;

    const abbrev = LINEUP_SLOT_MAP[slot]?.abbrev ?? `slot ${slot}`;

    // Dedicated table first, then the two derived rules. The rules partition —
    // QB (1) is required for superflex and disqualifies a slot from flex, and
    // verifyEspnConstants pins that no slot answers true to both — so the
    // order between them is readability, not precedence.
    const key = SLOT_TO_POSITION[slot]
      ?? (isSuperflexSlot(slot) ? 'SFLEX' : isFlexSlot(slot) ? 'FLEX' : null);
    if (key) {
      // SUM, never overwrite: several ESPN slots can feed one Mock1 position.
      // This is no longer hypothetical for FLEX — a league with slot 3 (RB/WR)
      // and slot 23 (FLEX) lands both here.
      positions[key] += count;

      // A flex slot narrower than Mock1's RB/WR/TE was WIDENED to fit. The
      // roster size is right and the eligibility is approximate; say which.
      // Mapping only slot 23 and dropping these instead is the bug this
      // replaced — a 12-team league with one RB/WR slot imported 12 starters
      // short, changing total demand and repricing the whole auction
      // (provenance §1). A right-sized roster with generalized eligibility
      // beats a right-eligibility roster of the wrong size.
      const eligible = LINEUP_SLOT_MAP[slot]?.eligiblePositions ?? [];
      if (key === 'FLEX' && !MOCK1_FLEX_POSITION_IDS.every((p) => eligible.includes(p))) {
        positionNotes.push(
          `Roster: ${count}× ${abbrev} slot was imported as FLEX. Mock1's FLEX admits `
          + `RB/WR/TE, so it is slightly more permissive than ESPN's ${abbrev} `
          + `restriction — the roster size is right, the eligibility is approximate.`
        );
      }
      continue;
    }

    // Everything else — IDP, P, HC, TQB, EDR — has no Mock1 equivalent at all.
    // Dropping one silently would shrink the roster and reprice the whole
    // draft, so say so instead.
    positionNotes.push(
      `Roster: ${count}× ${abbrev} slot has no Mock1 equivalent and was dropped. `
      + `Imported roster size is ${count} smaller than the real league's.`
    );
  }

  return { positions, positionNotes };
}

// ── Fetch orchestration ────────────────────────────────────────────────────
//
// Everything above this line is pure: settings in, settings out, no I/O. This
// section is the only part that touches the network, and it is the first code
// that JOINS the pieces — until now parseEspnSettings and parseEspnDraft had no
// caller at all.
//
// Both endpoints are same-origin proxies (api/espn/*), so credentials never
// leave for ESPN from the browser and are never persisted; see the contract
// comment in api/espn/league.js.

/**
 * Fetch and parse ONE ESPN season into a saved-league season record.
 *
 * @param leagueId ESPN league id.
 * @param year     Season year. Coerced with Number() before the mismatch
 *                 comparison below — a string "2025" from a URL or an input
 *                 field would otherwise never equal the numeric seasonId the
 *                 proxy returns, and every import would read as a mismatch.
 * @param options  { espnS2, swid } — both optional. A public league needs
 *                 neither.
 *
 * NEVER THROWS. Returns { season, error, status } in every path, one uniform
 * shape rather than a union the caller has to sniff: `season` is null exactly
 * when `error` is non-null. This mirrors fetchYahooLeagueHistory /
 * fetchSleeperLeagueHistory, which also report failure by return value — an
 * import that dies on an exception loses the seasons that already succeeded.
 *
 * Error values: whatever the proxy reported ('auth', 'host', an upstream
 * status message), plus three this function decides on its own —
 * 'season-mismatch'      ESPN answered with a different year.
 * 'players-unavailable'  the player lookup failed. Transient, retryable.
 * 'players-unmatched'    the lookup worked and resolved nothing. Not retryable.
 * They exist for the same reason: an import that appears to succeed while
 * carrying wrong or absent history is worse than one that visibly fails.
 *
 * `season`, `error` and `status` are present on every return. The two
 * players-* errors additionally carry `cause` ('http' | 'network' | null) so a
 * caller can log WHY the lookup failed without this function minting a
 * separate error name per transport failure; it is undefined elsewhere.
 * `status` belongs to whichever call failed — the players call for those two,
 * the league call otherwise.
 */
export async function fetchEspnLeagueSeason(leagueId, year, options = {}) {
  const { espnS2, swid } = options;
  const requestedYear = Number(year);

  const params = new URLSearchParams({ leagueId: String(leagueId), year: String(year) });
  // BOTH or neither, matching the proxy: it builds the Cookie header only when
  // it has the pair, because a half pair reads as a malformed session and ESPN
  // 401s a public league that would otherwise have loaded anonymously.
  if (espnS2 && swid) {
    params.set('espnS2', espnS2);
    params.set('swid', swid);
  }

  let payload = null;
  let status = null;
  try {
    const r = await fetch(`/api/espn/league?${params.toString()}`);
    status = r.status;
    payload = await r.json().catch(() => null);
    if (!r.ok) {
      // The proxy's own vocabulary, passed through untranslated: 'auth' means
      // the cookies need re-pasting, 'host' means our path is wrong. A caller
      // showing the user a message needs to tell those apart.
      return {
        season: null,
        error: payload?.error || `ESPN league request failed (${r.status})`,
        status,
      };
    }
  } catch (e) {
    return { season: null, error: e?.message || 'network error', status };
  }

  // ── SEASON CROSS-CHECK ──────────────────────────────────────────────────
  //
  // The ONLY guard against silently importing the wrong year. Seasons ≤2017 go
  // through the leagueHistory path, which takes seasonId as a QUERY PARAM
  // rather than in the URL — so a request for a season the league did not play
  // can come back 200 with a different year's payload, and every downstream
  // layer would happily parse it. A wrong-season import is worse than a failed
  // one: it looks like real history and quietly corrupts the price data.
  //
  // Only enforced when both sides are known. A null seasonId means the proxy
  // had nothing to report, which is not evidence of a mismatch.
  const seasonId = payload?.seasonId ?? null;
  if (seasonId !== null && Number.isFinite(requestedYear) && seasonId !== requestedYear) {
    return { season: null, error: 'season-mismatch', status };
  }

  // ── PLAYER LOOKUP — carries POSITION, not just names ────────────────────
  //
  // mDraftDetail carries playerId only, so this second call supplies both
  // halves of what a pick needs: api/espn/players.js returns
  // { name, posId }. THE POSITION IS THE LOAD-BEARING HALF, and that is why
  // this lookup is not the pure enrichment its endpoint comment describes.
  // "A name lookup is enrichment" is true of the NAME. It is not true of the
  // response as a whole.
  //
  // The two halves fail differently, and the split is deliberate:
  //
  //   PARTIAL map  → non-fatal. Every id that resolved keeps its position and
  //                  price; a resolved entry with a null name degrades to
  //                  String(playerId). Ids that resolved to nothing drop with
  //                  the existing summary note, which names the count.
  //   EMPTY map    → HARD ERROR when there were picks to resolve. parseEspnDraft
  //                  correctly refuses a null pos, so an empty map drops EVERY
  //                  pick. Parsing on would return a season that reports
  //                  success while delivering no auction history at all — and
  //                  worse, a contributed empty season into /api/auction-history,
  //                  which validates fine because zero picks is structurally
  //                  legal. A silent nothing is the one outcome not worth
  //                  degrading to.
  //
  // A draft with NO PICKS TO BEGIN WITH is a different thing and stays valid:
  // an undrafted or empty league legitimately has no picks, the lookup is
  // skipped entirely, and an empty season is the honest answer rather than a
  // failure. The guards below key on `playerIds.length`, not on the map alone,
  // precisely to keep those two cases apart.
  const draftDetail = payload?.draftDetail ?? null;
  const rawPicks = Array.isArray(draftDetail?.picks) ? draftDetail.picks : [];
  const playerIds = [...new Set(
    rawPicks.map((p) => p?.playerId).filter((id) => id !== null && id !== undefined),
  )];
  const lookup = playerIds.length
    ? await fetchEspnPlayerNames(year, playerIds)
    : { players: {}, error: null, status: null };
  const players = lookup.players;

  // ── TWO FAILURES, NOT ONE ───────────────────────────────────────────────
  //
  // Both end with no usable picks, and a single 'players-lookup-failed' told
  // the user nothing about which. They need OPPOSITE responses:
  //
  //   players-unavailable  the lookup itself failed ('http' | 'network').
  //                        TRANSIENT and RETRYABLE — ESPN's player service was
  //                        down, or the browser was offline. "Try again" is
  //                        correct advice and reporting it is noise.
  //   players-unmatched    the lookup SUCCEEDED and resolved nothing for ids
  //                        that came out of this league's own draft. Retrying
  //                        does not help; something is genuinely wrong (a bad
  //                        season for the id space, an upstream shape change),
  //                        and it should be reported rather than re-attempted.
  //
  // C5 writes the user-facing copy and picks the button, so the distinction has
  // to exist in the data first — copy cannot recover a difference the return
  // value already threw away.
  //
  // `status` is the PLAYERS call's, not the league call's: the league request
  // succeeded in both branches, so reporting its 200 would be actively
  // misleading in a log. It is null for 'network', which never got a response.
  // `cause` carries the helper's own classification through, so a caller can
  // log 'http' vs 'network' without this function inventing a third error name.
  if (playerIds.length && lookup.error) {
    return {
      season: null,
      error: 'players-unavailable',
      cause: lookup.error,
      status: lookup.status,
    };
  }
  if (playerIds.length && !Object.keys(players).length) {
    return {
      season: null,
      error: 'players-unmatched',
      cause: null,
      status: lookup.status,
    };
  }

  // seasonId first: it is what ESPN actually returned, and the mismatch guard
  // above has already proved it agrees with the request when both are known.
  const settings = parseEspnSettings(payload?.settings, seasonId ?? requestedYear);
  const { picks, notes: draftNotes, droppedPicks } = parseEspnDraft(draftDetail, players);

  return {
    season: {
      ...settings,
      picks,
      // Carried alongside the picks so a caller can report how complete the
      // import actually was without re-deriving it from the note text.
      droppedPicks,
      // AFTER the spread — settings carries its own `notes`, and this must
      // replace it with the union rather than be shadowed by it. Roster and
      // scoring caveats and dropped-pick caveats are both things the user
      // needs to see, in one list.
      notes: [...settings.notes, ...draftNotes],
    },
    error: null,
    status,
  };
}

// Errors fetchEspnLeagueSeason mints itself, as opposed to passing through
// from the proxy. Used to keep the advance-and-retry below strictly about a
// missing LEAGUE season: the players endpoint can also 404, and that must not
// be mistaken for "this year does not exist".
const SELF_MINTED_ERRORS = new Set([
  'season-mismatch', 'players-unavailable', 'players-unmatched',
]);

// Did the walk fail at its FIRST season because ESPN has no such season?
//
// 404 from the LEAGUE call only. Every other depth-0 failure — auth (401),
// host (502), season-mismatch, players-* — describes something a different
// year cannot fix, so none of them advance.
function isLeagueSeasonNotFound(result, startYear) {
  return result.seasons === null
    && result.status === 404
    && result.stoppedAt === startYear
    && !SELF_MINTED_ERRORS.has(result.error);
}

/**
 * Walk an ESPN league backwards through its seasons.
 *
 * Mirrors fetchSleeperLeagueHistory: same { seasons, error } return, same
 * onProgress(message, current, total), same maxDepth default of 20, and the
 * same rule that a failure at the entry point is fatal while a failure deeper
 * in just ends the history.
 *
 * ONE STRUCTURAL DIFFERENCE. Sleeper hands you previous_league_id and you
 * follow the chain; ESPN league ids PERSIST across seasons, so there is no
 * chain to follow. The walk is `startYear`, `startYear - 1`, … and the only
 * thing that ends it is a season that fails to come back.
 *
 * options:
 *   startYear  first season to try. Default is the CALENDAR year, which
 *              advances once on a depth-0 404 — see the block in the body for
 *              why, and why an explicit value never advances.
 *   maxDepth   cap on walkback length (default 20)
 *   espnS2 / swid  credentials, forwarded per season. Both or neither.
 *   onProgress callback (message, current, total)
 *
 * Returns { seasons, error, stoppedAt, stoppedBecause, startedAt,
 * advancedFrom, notes }. `startedAt` is the year actually walked from and
 * `advancedFrom` the year abandoned to get there (null when no advance
 * happened), so a caller can distinguish "started at 2025 because 2026 does
 * not exist yet" from "started at 2025 because that is what was asked for".
 *
 * `seasons` is newest-first. IT CAN BE NON-NULL ALONGSIDE A NON-NULL `error`:
 * that is the "we got some, then hit something you need to know about" case,
 * and collapsing it to one or the other would either hide the failure or throw
 * away real history. `seasons` is null only when nothing was collected.
 *
 * NO withCache, DELIBERATELY. Sleeper caches because its walk is many requests
 * per season (league, rosters, users, matchups × 18, drafts, …), so a re-walk
 * is genuinely expensive. ESPN is TWO requests per season. Caching that would
 * add a staleness class — a cached in-progress draft that never updates — for
 * a saving that does not exist yet. Revisit if per-season cost grows.
 */
export async function fetchEspnLeagueHistory(leagueId, options = {}) {
  const maxDepth = options.maxDepth ?? 20;
  const onProgress = options.onProgress || (() => {});
  const { espnS2, swid } = options;

  if (!leagueId || !/^\d+$/.test(String(leagueId).trim())) {
    return {
      seasons: null, error: 'Invalid league ID',
      stoppedAt: null, stoppedBecause: null,
      startedAt: null, advancedFrom: null, notes: [],
    };
  }

  const id = String(leagueId).trim();

  // ── WHERE TO START, AND WHY IT IS THE CALENDAR YEAR ─────────────────────
  //
  // The CURRENT season is where the league's ACTIVE settings live — teams,
  // budget, lineupSlotCounts, scoring — and those settings are what configure
  // the mock. Starting a year back imports STALE SETTINGS for any league that
  // changed format over the offseason, with no signal that it happened: the
  // import succeeds, the almanac looks complete, and the mock is built to last
  // year's roster. That is a worse failure than a wasted request.
  //
  // Verified live Jul 28 2026: league 42654852 returns 200 for season 2026, so
  // the rollover has already happened mid-summer and a last-completed-season
  // default would have skipped the active year outright.
  //
  // The cost is that some leagues genuinely have not rolled over yet, and
  // depth 0 is fatal — so a 404 on the very first try ADVANCES ONCE and
  // retries from the year before. That converts the only case the calendar
  // year gets wrong into one extra request.
  //
  // An UNDRAFTED current season is not this case and needs no handling here:
  // it returns 200 with zero picks, is kept, and does not end the walk.
  const explicitStart = options.startYear != null;
  const firstStart = explicitStart ? options.startYear : new Date().getFullYear();

  let result = await walkFrom(firstStart);
  let advancedFrom = null;
  const notes = [];

  // ONLY the default advances. An explicit startYear is an instruction, not a
  // guess — silently walking a year the caller did not ask for would make
  // their own request unreproducible.
  if (!explicitStart && isLeagueSeasonNotFound(result, firstStart)) {
    advancedFrom = firstStart;
    notes.push(
      `Season ${firstStart} does not exist for this league yet, so the walk `
      + `started at ${firstStart - 1} instead.`
    );
    result = await walkFrom(firstStart - 1);
  }

  return {
    ...result,
    // The year actually walked from, and the one abandoned to get there. A
    // caller showing "2019-2025" needs to know whether 2025 is where it was
    // told to start or where it ended up — and, when the advance happened,
    // that the missing current year is expected rather than lost data.
    startedAt: advancedFrom !== null ? firstStart - 1 : firstStart,
    advancedFrom,
    notes: [...notes, ...(result.notes ?? [])],
  };

  // One pass down the years from `start`. Returns the same
  // { seasons, error, stoppedAt, stoppedBecause } shape the caller gets; the
  // wrapper above only ever runs it twice, and only for the 404 case.
  async function walkFrom(startYear) {
    const seasons = [];

    for (let depth = 0; depth < maxDepth; depth++) {
      const year = startYear - depth;
      onProgress(`Fetching ${year}...`, depth, maxDepth);

      const res = await fetchEspnLeagueSeason(id, year, { espnS2, swid });

      if (!res.error) {
        seasons.push(res.season);
        onProgress(`Loaded ${year}`, depth + 1, maxDepth);
        continue;
      }

      // Collected so far, or null when the very first season failed. Every
      // exit below shares this so "nothing collected" is one consistent answer.
      const collected = seasons.length ? seasons : null;

      // ── STOP AND SURFACE, at ANY depth ──────────────────────────────────
      //
      // These two are NOT end-of-history. Truncating on them silently would
      // bake a temporary condition into a permanent import — the user would
      // end up with an almanac missing seasons that exist, and no reason given.
      //
      // auth: cookies expiring mid-walk is NORMAL, not exceptional — an ESPN
      // session outlives a short walk but not always a 20-season one. The
      // caller needs to prompt for a re-paste and resume, which a generic
      // failure message cannot ask for.
      //
      // players-unavailable: transient by construction (see
      // fetchEspnPlayerNames — it means the player service failed, not that
      // the season is absent). The caller must be able to offer a retry, so it
      // cannot look like the end of the league's history.
      if (res.error === 'auth' || res.error === 'players-unavailable') {
        return {
          seasons: collected,
          error: res.error,
          cause: res.cause,
          status: res.status,
          stoppedAt: year,
          stoppedBecause: res.error,
        };
      }

      // ── DEPTH 0 IS FATAL ────────────────────────────────────────────────
      //
      // The entry point failing means we never established the league exists
      // at all. Returning an empty list would hand the user an empty almanac
      // that looks like a league with no history, rather than the failure it
      // is.
      //
      // A 404 here is the ONE case the caller retries — see the advance above,
      // which only fires for the default start year and only once.
      if (depth === 0) {
        return {
          seasons: null,
          error: res.error,
          cause: res.cause,
          status: res.status,
          stoppedAt: year,
          stoppedBecause: res.error,
        };
      }

      // ── DEEPER: END OF HISTORY ──────────────────────────────────────────
      //
      // A 404, a season-mismatch (ESPN answered with a year we did not ask
      // for, which is how the leagueHistory path reports "not this far back"),
      // an unmatched player set, or anything else — the league simply does not
      // go back further, and what we have is the answer. `stoppedBecause`
      // records which, so a caller can tell "ran out of seasons" from "hit a
      // problem" without either becoming an error the user has to act on.
      return {
        seasons,
        error: null,
        stoppedAt: year,
        stoppedBecause: res.error,
      };
    }

    // Ran the full depth without a stop — the league may go back further.
    return { seasons, error: null, stoppedAt: null, stoppedBecause: 'max-depth' };
  }
}

/**
 * Player ids → { players, error, status }.
 *
 *   players  { [id]: { name, posId } }. Whatever RESOLVED, always — the proxy
 *            skips failed chunks and returns the rest, and a partial map is
 *            useful, so this is never discarded. {} when nothing came back.
 *   error    null | 'http' | 'network'
 *   status   the PLAYERS call's HTTP status, or null when the request never
 *            got far enough to have one (a throw).
 *
 * IT RETURNS A REASON, NOT JUST A MAP. The earlier version collapsed every
 * failure into a bare {}, which made "the service is down" and "we asked and
 * nothing matched" indistinguishable at the call site — and those two want
 * opposite responses from the user. Distinguishing them is the whole point of
 * this shape; do not narrow it back to the map.
 *
 * WHERE THE LINE SITS. 'http' covers both a non-ok status and a 200 whose body
 * cannot be read, because both mean the SERVICE failed to answer properly —
 * retryable. Only a well-formed response that genuinely resolved nothing is
 * error: null, because that is a fact about the data, not a fault. Classifying
 * an unreadable body as "nothing matched" would tell a user to report a bug
 * when the right advice is to try again.
 *
 * Ids travel in the query string, so the request is bounded by URL length
 * rather than the proxy's own 200-id chunking: a 16-round 12-team draft is
 * ~192 ids ≈ 1.5KB, comfortably inside any limit. No credentials — the player
 * universe is public even when the league is not.
 */
async function fetchEspnPlayerNames(year, playerIds) {
  try {
    const params = new URLSearchParams({
      year: String(year),
      playerIds: playerIds.join(','),
    });
    const r = await fetch(`/api/espn/players?${params.toString()}`);
    if (!r.ok) return { players: {}, error: 'http', status: r.status };

    const body = await r.json().catch(() => null);
    // A 200 we cannot parse is a service fault, not an empty result set.
    if (!body || typeof body !== 'object') {
      return { players: {}, error: 'http', status: r.status };
    }
    // A well-formed response with no `players` key is the same fault: the
    // endpoint always emits one, even when it resolved nothing.
    if (!body.players || typeof body.players !== 'object') {
      return { players: {}, error: 'http', status: r.status };
    }
    return { players: body.players, error: null, status: r.status };
  } catch {
    // No response at all — offline, DNS, CORS, aborted. There is no status.
    return { players: {}, error: 'network', status: null };
  }
}

// One scope's walk over scoringItems. `readValue` pulls the point value from
// whichever field that scope uses.
//
// FIRST WRITE WINS, and a later disagreeing write is recorded in `writes`
// instead of replacing it. Several distinct ESPN events collapse onto one
// Mock1 key — twoPtConv ← 19 (pass) / 26 (rush) / 44 (reception), returnTD ←
// 101 / 102, defTD ← 93 / 101 / 102 / 103 / 104 — and that is a representation
// limit, not a fallback chain, so these are NOT marked supersededBy
// (provenance §7.4). Normally every id in a group carries the same value and
// the choice is moot; a league scoring them differently cannot be represented
// by Mock1 at all. No precedence order fixes that — it only makes the
// arbitrary choice repeatable — so take the first and emit a note. That turns
// a silent arbitrary choice into a known one.
function applyScoringPass(scoring, scoringItems, presentIds, statMap, readValue, writes) {
  for (const item of scoringItems) {
    const id = Number(item?.statId);
    const entry = statMap[id];
    if (!entry) continue;

    // Fallback entries defer to a finer-grained id whenever that id is
    // scored anywhere in this league.
    if (entry.supersededBy !== undefined && presentIds.has(entry.supersededBy)) continue;

    const raw = readValue(item);
    if (raw === null || raw === undefined) continue;
    const value = Number(raw);
    if (isNaN(value)) continue;

    // 'yds' entries hold ESPN's points-per-yard (0.04 = 1pt per 25 yds);
    // Mock1 stores ydsPer (25). Same conversion Yahoo uses.
    const resolved = entry.type === 'yds'
      ? (value !== 0 ? Math.round(1 / value) : null)
      : value;
    if (resolved === null) continue;

    // keys is ALWAYS an array — one element for almost every stat, three for
    // statId 80 (ESPN's 0-39 bucket spans three Mock1 keys). There is no
    // string form to special-case.
    for (const key of entry.keys) {
      const prior = writes.get(key);
      if (!prior) {
        scoring[key] = resolved;
        writes.set(key, { statId: id, value: resolved, conflictingIds: [], conflictingValues: [] });
      } else if (prior.value !== resolved) {
        prior.conflictingIds.push(id);
        prior.conflictingValues.push(resolved);
      }
    }
  }
}
